import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth';
import { ToastProvider } from './lib/toast';
import { LoginPage } from './pages/Login';
import { OverviewPage } from './pages/Overview';
import { JobsPage } from './pages/Jobs';
import { JobDetailPage } from './pages/JobDetail';
import { UsersPage } from './pages/Users';
import { UserDetailPage } from './pages/UserDetail';
import { RatingsPage } from './pages/Ratings';
import { StuckSignupsPage } from './pages/StuckSignups';
import { SupportPage } from './pages/Support';
import { AnnouncementsPage } from './pages/Announcements';
import { OutreachPage } from './pages/Outreach';
import { LlmConfigPage } from './pages/LlmConfig';
import { SenderAccountsPage } from './pages/SenderAccounts';
import { IgPostsPage } from './pages/IgPosts';
import { Card, CardContent } from './components/ui/card';

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-bg">
        <p className="text-sm text-text-muted">Checking session…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-bg p-4">
        <Card className="max-w-md border-error/40 bg-error-tint">
          <CardContent className="p-6 text-center">
            <h2 className="text-lg font-semibold text-error-on-tint">Not authorised</h2>
            <p className="mt-2 text-sm text-error-on-tint/90">
              Your account doesn't have admin role. Ask another admin to promote you,
              or use the first-time-setup checkbox on the login page if no admins exist yet.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<RequireAdmin><OverviewPage /></RequireAdmin>} />
            <Route path="/jobs" element={<RequireAdmin><JobsPage /></RequireAdmin>} />
            <Route path="/jobs/:id" element={<RequireAdmin><JobDetailPage /></RequireAdmin>} />
            <Route path="/users" element={<RequireAdmin><UsersPage /></RequireAdmin>} />
            <Route path="/users/:id" element={<RequireAdmin><UserDetailPage /></RequireAdmin>} />
            <Route path="/ratings" element={<RequireAdmin><RatingsPage /></RequireAdmin>} />
            <Route path="/stuck-signups" element={<RequireAdmin><StuckSignupsPage /></RequireAdmin>} />
            <Route path="/support" element={<RequireAdmin><SupportPage /></RequireAdmin>} />
            <Route path="/announcements" element={<RequireAdmin><AnnouncementsPage /></RequireAdmin>} />
            <Route path="/outreach" element={<RequireAdmin><OutreachPage /></RequireAdmin>} />
            <Route path="/sender-accounts" element={<RequireAdmin><SenderAccountsPage /></RequireAdmin>} />
            <Route path="/llm-config" element={<RequireAdmin><LlmConfigPage /></RequireAdmin>} />
            <Route path="/ig-posts" element={<RequireAdmin><IgPostsPage /></RequireAdmin>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
